import clsx from 'clsx';
import { forwardRef, type AriaRole, type MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';

import { ExternalLinkIcon } from '@dynatrace/strato-icons';

import * as styles from './ExternalLink.sty.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';

const COMPONENT_NAME = 'ExternalLink';

const SAFE_PROTOCOLS = new Set(['https:', 'http:', 'mailto:', 'tel:', 'ftp:']);

const sanitizeHref = (href: string): string | undefined => {
  const trimmed = href.trim();
  try {
    const { protocol } = new URL(trimmed);
    return SAFE_PROTOCOLS.has(protocol) ? trimmed : undefined;
  } catch {
    // Relative URL — allow only if there is no colon at all, or the string
    // starts with a path/query/hash separator. A colon in any other position
    // could indicate an opaque or unknown scheme (e.g. "scheme:path").
    return /^([^:]*$|[/?#])/.test(trimmed) ? trimmed : undefined;
  }
};

/**
 * The props for the ExternalLink component.
 * @public
 */
export interface ExternalLinkProps
  extends WithChildren,
    DOMProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /** The href used for the link. */
  href: string;
  /** Explicit ARIA role for the rendered anchor element. */
  role?: AriaRole;
  /** Callback handler called on click. */
  onClick?: MouseEventHandler;
}

/**
 * Use the `ExternalLink` component to link to resources outside the user's current Dynatrace environment, such as Dynatrace documentation, or third-party sites. It always opens in a new tab and displays an external link icon.
 * @public
 */
export const ExternalLink = /* @__PURE__ */ forwardRef<
  HTMLAnchorElement,
  ExternalLinkProps
>((props, ref) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    href,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  const intl = useIntl();

  return (
    <a
      ref={ref}
      href={sanitizeHref(href)}
      target="_blank"
      rel="noopener noreferrer"
      className={clsx(styles.externalLink, consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      {children}
      <span style={{ userSelect: 'none' }}>
        &#65279;
        <ExternalLinkIcon
          aria-hidden="false"
          // https://www.w3.org/WAI/tutorials/images/functional/#example-3-icon-image-conveying-information-within-link-text
          aria-label={intl.formatMessage({
            defaultMessage: 'Opening the link in a new window',
            id: 'JzYy8MUVfPcyf4l5',
            description:
              'External link icon that describes the external link icon',
          })}
        />
      </span>
    </a>
  );
});

(ExternalLink as typeof ExternalLink & { displayName: string }).displayName =
  COMPONENT_NAME;
